import {
  bundle,
  latestAtViewport,
  makeColumns,
  resolveTableColumns,
  getChannelDef,
  getChannelWarn,
  getChannelCrit,
  getChannelDir,
  colKey,
  tableColDef,
  tableColLabel,
  alertLevel,
  findTelemetrySeriesStore,
  getWidgetLatestTimestamp,
  getColLatestAtViewport,
  getTelemetrySeriesColor,
  CHART_MAX_UNITS,
  fmtWinTime,
  fmtTooltipTimestamp,
  conv,
  unitOf,
  getPointTmType,
  getGlobalSimulationStartTimeMs,
  getTelemetryEarliestTimestamp,
} from './telemetry-helpers.utils';
import { cleanUnit } from './telemetry-unit.utils';
import { resolveEnumValue, isBooleanEnumState, makeEnumBadgeHtml } from './telemetry-enum.utils';
import { calculateViewportBoundedTooltipPosition } from './telemetry-tooltip.utils';

/** Base axis styling used across time-series, bar, and categorical axes */
export const baseAxis = () => ({
  axisLine: {
    lineStyle: { color: '#313131' },
  },
  axisTick: {
    show: false,
  },
  axisLabel: {
    color: '#B4B4B4',
    fontFamily: 'Fira Mono',
    fontSize: 10,
  },
  splitLine: {
    lineStyle: { color: 'rgba(255,255,255,.05)', type: 'dashed' },
  },
});

/** Dark glassmorphic tooltip styling template with adaptive boundary-safe positioning */
export const tooltipStyle = () => ({
  backgroundColor: '#0d1318',
  borderColor: '#242f38',
  borderWidth: 1,
  className: 'telemetry-echarts-tooltip',
  textStyle: { color: '#D4D4D4', fontSize: 11, fontFamily: 'Fira Mono' },
  padding: [6, 10],
  appendToBody: true,
  confine: false,
  position: calculateViewportBoundedTooltipPosition,
  extraCssText: 'z-index: 99999; pointer-events: auto; max-height: 250px; overflow-y: auto; box-shadow: 0 8px 24px rgba(0,0,0,0.6); border-radius: 6px; scrollbar-color: #666666 transparent; scrollbar-width: thin;',
});


/**
 * Normalizes raw telemetry event types or fallback types into clean, user-facing TM source badges.
 * Internal WebSocket transport strings like 'GraphTelemetry' or 'message' are safely resolved to 'TM'.
 */
export function normalizeTmType(tmType?: string): string {
  if (!tmType || tmType === 'GraphTelemetry' || tmType === 'message') {
    return 'TM';
  }
  return String(tmType).trim();
}

/** Tooltip HTML helper: marker dot */
export const makeTooltipMarker = (color: string) =>
  `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background-color:${color};"></span>`;

/** Tooltip HTML helper: tmType badge pill */
export const makeTmTypeBadge = (tmType?: string): string => {
  const cleanType = normalizeTmType(tmType);
  return `<span style="font-size:9px;color:#00E5FF;background:rgba(0,229,255,0.12);padding:1px 4px;border-radius:2px;margin-left:4px;font-weight:600;">${cleanType}</span>`;
};

/** Tooltip HTML helper: header time/category bar with sticky positioning on scroll */
export const makeTooltipHeader = (title: string, extraRightContent?: string) =>
  `<div class="telemetry-tooltip-sticky-header" style="position:sticky;top:-6px;z-index:2;background:#0d1318;padding-top:4px;padding-bottom:6px;margin-bottom:6px;font-weight:600;color:#EEE;border-bottom:1px solid rgba(255,255,255,0.12);display:flex;align-items:center;justify-content:space-between;">` +
  `<span>${title}</span>` +
  (extraRightContent || '') +
  `</div>`;

/** Tooltip HTML helper: item row */
export const makeTooltipRow = (marker: string, title: string, badge: string, valueStr: string) =>
  `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:3px;">` +
  `<span>${marker}${title}${badge}</span>` +
  `<span style="font-weight:600;color:#FFF;font-family:'Fira Mono',monospace;">${valueStr}</span>` +
  `</div>`;

/**
 * Intelligently formats parameter and series labels for chart tooltips,
 * eliminating duplicate and redundant naming (e.g. "RW_Speed1 - RW_Speed1 0" -> "RW_Speed1 0",
 * "sun vector - sun vector" -> "sun vector").
 */
export function formatTooltipSeriesTitle(
  paramLabel?: string,
  seriesName?: string,
  fallbackName?: string
): string {
  const pLabel = (paramLabel || '').trim();
  const sName = (seriesName || '').trim();
  const fName = (fallbackName || '').trim();

  if (!pLabel && !sName) return fName || 'Parameter';
  if (!sName) return pLabel;
  if (!pLabel) return sName;

  const pLower = pLabel.toLowerCase();
  const sLower = sName.toLowerCase();

  // 1. Identical labels (case-insensitive) -> avoid duplication (e.g. "sun vector X component - sun vector X component")
  if (pLower === sLower) {
    return pLabel;
  }

  // 2. seriesName already contains paramLabel as prefix (e.g. param: "RW_Speed1", series: "RW_Speed1 0")
  if (sLower.startsWith(pLower)) {
    return sName;
  }

  // 3. paramLabel already ends with seriesName
  if (pLower.endsWith(sLower)) {
    return pLabel;
  }

  // 4. seriesName is purely an index or array bracket (e.g. "0", "1", "[0]")
  if (/^\[?\d+\]?$/.test(sName)) {
    return `${pLabel} ${sName.replace(/[[\]]/g, '')}`;
  }

  // 5. Distinct series under parameter (e.g. param: "Angular Rate", series: "Roll")
  return `${pLabel} - ${sName}`;
}

/** SVG Path Icons for ECharts Toolbox */
export const TOOLBOX_ZOOM_ICON =
  'path://M6.5 1a5.5 5.5 0 1 0 3.38 9.83l4.14 4.15a1 1 0 0 0 1.42-1.42l-4.15-4.14A5.5 5.5 0 0 0 6.5 1zm0 2a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z';

export const TOOLBOX_RESET_ICON =
  'path://M12 4V1L8 5l4 4V6c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6H4c0 4.4 3.6 8 8 8s8-3.6 8-8-3.6-8-8-8z';

/** Centralized ECharts Toolbox configuration builder */
export function getToolboxConfig(left: string | number = 85, top: string | number = 0) {
  return {
    show: true,
    left: -9999,
    top: -9999,
    itemSize: 0,
    iconStyle: {
      opacity: 0,
      borderColor: '#7B7B7B',
    },
    emphasis: {
      iconStyle: {
        borderColor: '#CCF54E',
      },
    },
    feature: {
      dataZoom: {
        yAxisIndex: 'none',
        title: {
          zoom: 'Zoom Select',
          back: '',
        },
        icon: {
          zoom: TOOLBOX_ZOOM_ICON,
          back: 'none',
        },
      },
    },
  };
}

/** Centralized ECharts Tooltip configuration builder */
export function createChartTooltip(
  precision: number,
  defaultDef?: any,
  units?: any,
  meta?: any[],
  span?: number
) {
  return {
    trigger: 'axis',
    enterable: true,
    ...tooltipStyle(),
    axisPointer: { lineStyle: { color: '#516025' } },
    formatter: (items: any[]) => {
      if (!Array.isArray(items) || items.length === 0) return '';
      const rawT = items[0].value?.[0] ?? items[0].axisValue;
      let headerTime = '';
      if (typeof rawT === 'number') {
        headerTime = fmtTooltipTimestamp(rawT, span);
      } else if (typeof rawT === 'string' && !isNaN(Number(rawT)) && Number(rawT) > 1000000000) {
        headerTime = fmtTooltipTimestamp(Number(rawT), span);
      } else {
        headerTime = items[0].axisValueLabel || items[0].name || '';
      }

      let html = makeTooltipHeader(headerTime);
      items.forEach((item) => {
        const marker = makeTooltipMarker(item.color || '#00E5FF');
        const dataObj = item.data;
        const sDef = dataObj?._def || defaultDef;
        const badge = makeTmTypeBadge(dataObj?.tmType);
        const paramLabel = dataObj?.paramLabel || item.seriesName || 'Parameter';
        const seriesName = dataObj?.seriesName || '';
        const displayTitle = formatTooltipSeriesTitle(paramLabel, seriesName, item.seriesName);

        let rawVal = item.value;
        if (Array.isArray(rawVal)) {
          rawVal = rawVal[1];
        } else if (typeof rawVal === 'object' && rawVal !== null) {
          rawVal = rawVal.value;
        }

        const enumVal = resolveEnumValue(rawVal, sDef?.enumValues);
        const enumState = enumVal ? isBooleanEnumState(enumVal) : null;
        let valueStr = 'N/A';

        if (enumState) {
          valueStr = makeEnumBadgeHtml(enumState.toUpperCase(), enumState, rawVal);
        } else if (enumVal) {
          valueStr = `${enumVal}${rawVal != null ? ` (${rawVal})` : ''}`;
        } else {
          const rawUnitStr = dataObj?.unit || (sDef ? unitOf(sDef, units) : '') || meta?.find((m: any) => m.name === item.seriesName)?.unit || '';
          const uStr = cleanUnit(rawUnitStr);
          const formattedVal = rawVal != null ? (typeof rawVal === 'number' ? rawVal.toFixed(precision) : String(rawVal)) : 'N/A';
          valueStr = formattedVal + (uStr ? ' ' + uStr : '');
        }

        html += makeTooltipRow(marker, displayTitle, badge, valueStr);
      });
      return html;
    },
  };
}

/**
 * Enables interactive tooltip triggers when hovering directly over truncated
 * or full axis labels on categorical bar charts.
 */
export function attachAxisLabelTooltipHandler(ec: any) {
  if (!ec || typeof ec.on !== 'function') return;

  ec.on('mouseover', (params: any) => {
    if (params?.componentType === 'xAxis' || params?.componentType === 'yAxis') {
      try {
        const dom = typeof ec.getDom === 'function' ? ec.getDom() : null;
        if (dom && params.value) {
          dom.title = String(params.value);
        }
      } catch (_) { }
    }
  });

  ec.on('mouseout', (params: any) => {
    if (params?.componentType === 'xAxis' || params?.componentType === 'yAxis') {
      try {
        const dom = typeof ec.getDom === 'function' ? ec.getDom() : null;
        if (dom) {
          dom.removeAttribute('title');
        }
        ec.dispatchAction({
          type: 'hideTip',
        });
      } catch (_) { }
    }
  });
}

/** In-memory store to preserve user legend selections across horizontal scrolling and viewport updates */
const panelLegendSelectionMap: Record<string, Record<string, boolean>> = {};

export function getPanelLegendSelected(panelId: string): Record<string, boolean> | undefined {
  return panelLegendSelectionMap[panelId];
}

export function setPanelLegendSelected(panelId: string, selected: Record<string, boolean>) {
  if (!panelId || !selected) return;
  panelLegendSelectionMap[panelId] = {
    ...(panelLegendSelectionMap[panelId] || {}),
    ...selected,
  };
}

export function clearPanelLegendSelected(panelId?: string) {
  if (panelId) {
    delete panelLegendSelectionMap[panelId];
  } else {
    Object.keys(panelLegendSelectionMap).forEach((k) => delete panelLegendSelectionMap[k]);
  }
}

/** Resolves unified legend configuration positioned at the bottom right of the live viewport badge */
export function getLegendConfig(seriesList: any[]) {
  if (!seriesList || seriesList.length <= 1) {
    return { show: false };
  }

  const totalCharLen = seriesList.reduce((acc, s) => acc + (s?.name || '').length, 0);
  const isScroll = seriesList.length > 3 || totalCharLen > 45;

  return {
    show: true,
    type: isScroll ? 'scroll' : 'plain',
    bottom: 2,
    left: 160,
    right: 12,
    itemWidth: 8,
    itemHeight: 8,
    itemStyle: {
      borderRadius: 2,
    },
    textStyle: { color: '#B4B4B4', fontSize: 10, fontFamily: 'Montserrat' },
    pageIconColor: '#CCF54E',
    pageIconInactiveColor: '#444',
    pageTextStyle: { color: '#B4B4B4' },
    icon: 'roundRect',
  };
}

function extractThresholdNumber(val: any): number | null {
  if (typeof val === 'number' && !isNaN(val)) return val;
  if (Array.isArray(val) && val.length > 0) {
    const item = val[0];
    if (typeof item === 'number' && !isNaN(item)) return item;
    if (item && typeof item === 'object') {
      const num = item.min !== undefined && item.min !== null ? item.min : item.max;
      if (typeof num === 'number' && !isNaN(num)) return num;
    }
  }
  if (val && typeof val === 'object') {
    const num = val.min !== undefined && val.min !== null ? val.min : val.max;
    if (typeof num === 'number' && !isNaN(num)) return num;
  }
  return null;
}

/** Generates warning and critical threshold dashed lines for time-series charts */
export function thresholdMarkLine(
  paramOrW: any,
  def: any,
  units: any,
  paramLabel?: string
): any[] {
  if (!paramOrW) return [];
  const param = paramOrW.parameters ? paramOrW.parameters[0] : paramOrW;
  const th = param?.threshold;
  if (th?.on === false) return [];

  let warnVal = extractThresholdNumber(th?.warn) ?? extractThresholdNumber(param?.warn) ?? getChannelWarn(def);
  let critVal = extractThresholdNumber(th?.crit) ?? extractThresholdNumber(param?.crit) ?? getChannelCrit(def);

  if (warnVal == null && critVal == null) {
    if (def?.max != null && def?.min != null && def.max > def.min) {
      warnVal = def.min + (def.max - def.min) * 0.75;
      critVal = def.min + (def.max - def.min) * 0.90;
    } else if (def?.base != null) {
      const amp = Math.abs(def.amp || 5);
      warnVal = def.base + amp * 1.1;
      critVal = def.base + amp * 1.4;
    }
  }

  const data: any[] = [];
  const labelPrefix = paramLabel ? `${paramLabel} ` : '';

  if (warnVal != null) {
    const convWarn = conv(warnVal, def, units);
    if (convWarn != null && !isNaN(convWarn)) {
      data.push({
        yAxis: convWarn,
        lineStyle: { color: '#E79E2C', type: 'dashed', width: 1.2 },
        label: {
          formatter: (p: any) => {
            const val = typeof p?.value === 'number' ? p.value.toFixed(1) : '';
            return val ? `${labelPrefix}WARN · ${val}` : `${labelPrefix}WARN`;
          },
          color: '#E79E2C',
          fontSize: 9,
          fontFamily: 'Fira Mono',
          position: 'insideMiddleTop',
          padding: [2, 6],
          borderRadius: 3,
          backgroundColor: 'rgba(11, 17, 22, 0.85)',
        },
      });
    }
  }

  if (critVal != null) {
    const convCrit = conv(critVal, def, units);
    if (convCrit != null && !isNaN(convCrit)) {
      data.push({
        yAxis: convCrit,
        lineStyle: { color: '#FF4D4D', type: 'dashed', width: 1.2 },
        label: {
          formatter: (p: any) => {
            const val = typeof p?.value === 'number' ? p.value.toFixed(1) : '';
            return val ? `${labelPrefix}CRIT · ${val}` : `${labelPrefix}CRIT`;
          },
          color: '#FF8888',
          fontSize: 9,
          fontFamily: 'Fira Mono',
          position: 'insideMiddleTop',
          padding: [2, 6],
          borderRadius: 3,
          backgroundColor: 'rgba(11, 17, 22, 0.85)',
        },
      });
    }
  }

  return data;
}

/** Builds line or area chart options */
export function timeSeriesOption(
  w: any,
  isArea: boolean,
  timeframe: number,
  precision: number,
  units: any,
  viewport?: { spanMs: number; offsetMs: number }
): any {
  const b = bundle(w, timeframe, viewport);
  const def = b.def;
  const series = b.series;

  const ulist: string[] = [];
  series.forEach((s) => {
    const sDef = s._def || def;
    const u = cleanUnit(unitOf(sDef, units) || sDef?.unit);
    if (u && !ulist.includes(u) && ulist.length < CHART_MAX_UNITS) ulist.push(u);
  });

  const hidden = new Set(w.hiddenCols || []);

  const ecs = series
    .filter((s) => !hidden.has(s.name))
    .map((s, idx) => {
      const sDef = s._def || def;
      const u = cleanUnit(unitOf(sDef, units) || sDef?.unit);
      let yIndex = ulist.indexOf(u);
      if (yIndex < 0) yIndex = 0;

      const convertedData = s.data.map((item: any) => {
        if (typeof item === 'object' && item !== null) {
          const tMs = Array.isArray(item.value) ? item.value[0] : (item.tMs || 0);
          const rVal = Array.isArray(item.value) ? item.value[1] : item.rawVal;
          const cVal = conv(rVal, sDef, units);
          return {
            ...item,
            value: [tMs, cVal],
            rawVal: rVal,
          };
        }
        return item;
      });

      const paramForSeries = (w.parameters || []).find((p: any) => {
        const pSub = p.subsystem || '';
        const pId = p.paramId || p.paramName || '';
        const pLabel = p.label || (getChannelDef(pSub, pId) as any)?.label;
        return (
          p.uniqueId === s.name ||
          pLabel === s.name ||
          pId === s.name ||
          (pSub && pId && `${pSub}:${pId}` === s.name)
        );
      }) || (w.parameters && w.parameters[idx]) || w.parameters?.[0];

      const pLabel = (w.parameters || []).length > 1 ? (paramForSeries?.label || (s as any).paramLabel || s.name) : undefined;
      const thData = thresholdMarkLine(paramForSeries, sDef, units, pLabel);

      return {
        name: s.name,
        type: 'line',
        yAxisIndex: yIndex,
        data: convertedData,
        showSymbol: false,
        smooth: false,
        lineStyle: { width: 1.6, color: s.color },
        itemStyle: { color: s.color },
        areaStyle: isArea
          ? {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: s.color + '33' },
                { offset: 1, color: s.color + '00' },
              ],
            },
          }
          : undefined,
        markLine: thData.length > 0
          ? { silent: true, symbol: 'none', data: thData }
          : undefined,
      };
    });

  const yAxes =
    ulist.length > 0
      ? ulist.map((u, i) => {
        const pos = i % 2 === 0 ? 'left' : 'right';
        const off = i >= 2 ? 46 : 0;
        return {
          type: 'value',
          scale: true,
          position: pos,
          offset: off,
          name: u,
          nameGap: 8,
          nameLocation: 'end',
          nameTextStyle: {
            color: '#B4B4B4',
            fontFamily: 'Fira Mono',
            fontSize: 9,
            align: pos === 'left' ? 'left' : 'right',
          },
          ...baseAxis(),
          axisLabel: {
            color: '#B4B4B4',
            fontFamily: 'Fira Mono',
            fontSize: 9,
            margin: 6,
            formatter: (v: number) => v.toFixed(precision),
          },
        };
      })
      : [
        {
          type: 'value',
          scale: true,
          ...baseAxis(),
          axisLabel: {
            color: '#B4B4B4',
            fontFamily: 'Fira Mono',
            fontSize: 9,
            formatter: (v: number) => v.toFixed(precision),
          },
        },
      ];

  return {
    toolbox: getToolboxConfig(85, 0),
    grid: {
      left: 20,
      right: 32,
      top: 26,
      bottom: ecs.length > 1 ? 36 : 28,
      containLabel: true,
    },
    tooltip: createChartTooltip(precision, def, units, undefined, b.spanMs),
    legend: getLegendConfig(ecs),
    xAxis: {
      type: 'time',
      min: b.startMs ?? (b.endMs - b.spanMs),
      max: b.endMs,
      ...baseAxis(),
      axisLabel: {
        color: '#B4B4B4',
        fontFamily: 'Fira Mono',
        fontSize: 9,
        hideOverlap: true,
        formatter: (val: number) => fmtWinTime(val, b.spanMs),
      },
    },
    yAxis: yAxes,
    series: ecs,
  };
}

/** Multi-gauge renderer for multi-parameter and multi-series channels with 2D grid wrapping */
function multiGaugeOption(
  w: any,
  cols: Array<any>,
  precision: number,
  units: any,
  viewport?: { spanMs: number; offsetMs: number }
): any {
  const n = cols.length;

  // 2D Grid calculation: n <= 3: 1 row, n == 4: 2x2 grid, n >= 5: 3 columns with rows
  const gridCols = n <= 3 ? n : (n === 4 ? 2 : 3);
  const rows = Math.ceil(n / gridCols);

  const series = cols.map((c: any, i: number) => {
    const def = tableColDef(c);
    const nm = tableColLabel(c);
    const rawVal = getColLatestAtViewport(c, w, viewport);
    const v = conv(rawVal, def, units);

    let min = conv((def as any).gMin != null ? (def as any).gMin : (def.min != null ? def.min : 0), def, units)!;
    let max = conv(
      (def as any).gMax != null
        ? (def as any).gMax
        : (def.max != null
          ? def.max
          : Math.ceil(((def as any).base || 0 + ((def as any).amp || 0)) * 1.15) || 100),
      def,
      units
    )!;

    if (min >= max) {
      min = 0;
      max = 100;
    }

    if (v != null && typeof v === 'number' && !isNaN(v)) {
      if (v < min) {
        const step = 50;
        min = Math.floor(v / step) * step;
      }
      if (v > max) {
        const step = Math.abs(v) > 50 ? 50 : 10;
        max = Math.ceil(v / step) * step;
      }
    }

    const r = Math.floor(i / gridCols);
    const colIdx = i % gridCols;
    const itemsInThisRow = (r === rows - 1 && n % gridCols !== 0) ? (n % gridCols) : gridCols;
    const colCenterX = ((colIdx + 0.5) / itemsInThisRow) * 100;
    const rowCenterY = rows === 1 ? 50 : ((r + 0.5) / rows) * 100;

    const dialRadius = n === 2 ? 68 : n <= 4 ? 64 : 56;
    const dialColor = getTelemetrySeriesColor(i);

    const rawWarn = getChannelWarn(def);
    const rawCrit = getChannelCrit(def);
    const dir = getChannelDir(def);
    const warn = rawWarn != null ? conv(rawWarn, def, units) : null;
    const crit = rawCrit != null ? conv(rawCrit, def, units) : null;

    const norm = (x: number) => (x - min) / (max - min);
    let stops: [number, string][];
    if (warn != null || crit != null) {
      if (dir === 'low') {
        stops = [
          [crit != null ? norm(crit) : 0, '#FF3333'],
          [warn != null ? norm(warn) : 0, '#E79E2C'],
          [1, dialColor],
        ];
      } else {
        stops = [
          [warn != null ? norm(warn) : 0.7, dialColor],
          [crit != null ? norm(crit) : 0.85, '#E79E2C'],
          [1, '#FF3333'],
        ];
      }
      stops = stops
        .map((s) => [Math.max(0, Math.min(1, s[0])), s[1]] as [number, string])
        .sort((a, b) => a[0] - b[0]);
      if (!stops.length || stops[stops.length - 1][0] < 1) stops.push([1, dialColor]);
    } else {
      stops = [
        [0.7, dialColor],
        [0.85, '#E79E2C'],
        [1, '#FF3333'],
      ];
    }

    return {
      type: 'gauge',
      min,
      max,
      startAngle: 210,
      endAngle: -30,
      radius: dialRadius,
      center: [`${colCenterX}%`, `${rowCenterY}%`],
      progress: { show: false },
      pointer: { width: 2.5, length: '50%', itemStyle: { color: '#EEEEEE' } },
      anchor: { show: true, size: 5, itemStyle: { color: '#1E1E1E', borderColor: '#EEEEEE', borderWidth: 1.5 } },
      axisLine: {
        lineStyle: {
          width: 6,
          color: stops,
        },
      },
      axisTick: { show: false },
      splitLine: { distance: -8, length: 4, lineStyle: { color: 'rgba(255,255,255,0.2)', width: 1 } },
      axisLabel: {
        show: true,
        distance: -14,
        color: '#7B7B7B',
        fontSize: 8,
        fontFamily: 'Fira Mono',
        formatter: (val: number) => {
          if (val === min || val === max) {
            return Number(val.toFixed(0));
          }
          return '';
        },
      },
      detail: {
        valueAnimation: true,
        fontSize: 13,
        fontFamily: 'Space Grotesk',
        color: '#EEEEEE',
        offsetCenter: [0, '38%'],
        formatter: (x: number) => {
          const u = unitOf(def, units);
          return (typeof x === 'number' ? x.toFixed(precision) : x) + (u ? ' ' + u : '');
        },
      },
      title: {
        show: true,
        offsetCenter: [0, '94%'],
        fontSize: 11,
        fontFamily: 'Montserrat',
        color: '#B4B4B4',
        overflow: 'truncate',
      },
      data: [{ value: v, name: nm }],
    };
  });

  return {
    tooltip: {
      ...tooltipStyle(),
      formatter: (params: any) => {
        const val = typeof params.value === 'number' ? params.value.toFixed(precision) : params.value;
        const colItem = cols[params.seriesIndex];
        const u = colItem ? unitOf(tableColDef(colItem), units) : '';
        return `<div style="font-family: Montserrat, sans-serif; font-weight: 600; margin-bottom: 2px;">${params.name || 'Gauge'}</div><div>Value: <span style="color: #EEE; font-family: Fira Mono;">${val}${u ? ' ' + u : ''}</span></div>`;
      },
    },
    series,
  };
}

/** Builds gauge option object for single or multi-series gauge widgets */
export function gaugeOption(w: any, precision: number, units: any, timeframe: number, viewport?: { spanMs: number; offsetMs: number }): any {
  const cols = resolveTableColumns(w);
  if (!cols || cols.length === 0) {
    return { series: [] };
  }

  if (cols.length > 1) {
    return multiGaugeOption(w, cols, precision, units, viewport);
  }

  const c = cols[0];
  const def = tableColDef(c);
  const nm = tableColLabel(c);
  const v = conv(getColLatestAtViewport(c, w, viewport), def, units);
  let min = conv((def as any).gMin != null ? (def as any).gMin : (def.min != null ? def.min : 0), def, units)!;
  let max = conv((def as any).gMax != null ? (def as any).gMax : (def.max != null ? def.max : 100), def, units)!;

  if (min >= max) {
    min = 0;
    max = 100;
  }

  // Dynamically expand min/max cleanly if current value exceeds range so needle points accurately
  if (v != null && typeof v === 'number' && !isNaN(v)) {
    if (v < min) {
      const step = 50;
      min = Math.floor(v / step) * step;
      if (max > 0 && max <= 10) {
        max = 0; // snap positive tiny max to 0 for clean integer intervals
      }
    }
    if (v > max) {
      const step = Math.abs(v) > 50 ? 50 : 10;
      max = Math.ceil(v / step) * step;
      if (min >= 0 && min < 10) {
        min = 0;
      }
    }
  }

  const rawWarn = getChannelWarn(def);
  const rawCrit = getChannelCrit(def);
  const dir = getChannelDir(def);
  const warn = rawWarn != null ? conv(rawWarn, def, units) : null;
  const crit = rawCrit != null ? conv(rawCrit, def, units) : null;

  const norm = (x: number) => (x - min) / (max - min);
  let stops: [number, string][];

  if (dir === 'low') {
    stops = [
      [crit != null ? norm(crit) : 0, '#FF3333'],
      [warn != null ? norm(warn) : 0, '#E79E2C'],
      [1, '#CCF54E'],
    ];
  } else {
    stops = [
      [warn != null ? norm(warn) : 0.7, '#CCF54E'],
      [crit != null ? norm(crit) : 0.85, '#E79E2C'],
      [1, '#FF3333'],
    ];
  }
  stops = stops
    .map((s) => [Math.max(0, Math.min(1, s[0])), s[1]] as [number, string])
    .sort((a, b) => a[0] - b[0]);
  if (!stops.length || stops[stops.length - 1][0] < 1) stops.push([1, '#CCF54E']);

  return {
    tooltip: {
      ...tooltipStyle(),
      formatter: (params: any) => {
        const val = typeof params.value === 'number' ? params.value.toFixed(precision) : params.value;
        const u = unitOf(def, units);
        return `<div style="font-family: Montserrat, sans-serif; font-weight: 600; margin-bottom: 2px;">${params.name || nm || 'Gauge'}</div><div>Value: <span style="color: #EEE; font-family: Fira Mono;">${val}${u ? ' ' + u : ''}</span></div>`;
      },
    },
    series: [
      {
        type: 'gauge',
        min,
        max,
        splitNumber: (max - min) === 150 ? 3 : 4,
        startAngle: 210,
        endAngle: -30,
        radius: '70%',
        center: ['50%', '50%'],
        progress: { show: false },
        pointer: { width: 3, length: '52%', itemStyle: { color: '#EEEEEE' } },
        anchor: {
          show: true,
          size: 6,
          itemStyle: { color: '#1E1E1E', borderColor: '#EEEEEE', borderWidth: 1.5 },
        },
        axisLine: { lineStyle: { width: 7, color: stops } },
        axisTick: { show: false },
        splitLine: { distance: -10, length: 5, lineStyle: { color: 'rgba(255,255,255,0.2)', width: 1.2 } },
        axisLabel: {
          distance: -18,
          color: '#888888',
          fontSize: 9,
          fontFamily: 'Fira Mono',
          formatter: (val: number) => {
            if (Math.abs(val) >= 1000) return val.toFixed(0);
            return Number(val.toFixed(precision > 1 ? 1 : precision));
          },
        },
        detail: {
          valueAnimation: true,
          fontSize: 15,
          fontFamily: 'Space Grotesk',
          color: '#EEEEEE',
          offsetCenter: [0, '98%'],
          formatter: (x: number) => {
            const u = unitOf(def, units);
            return (typeof x === 'number' ? x.toFixed(precision) : x) + (u ? ' ' + u : '');
          },
        },
        title: {
          show: true,
          offsetCenter: [0, '68%'],
          fontSize: 12,
          fontFamily: 'Montserrat',
          color: '#B4B4B4',
          overflow: 'truncate',
        },
        data: [{ value: v, name: nm }],
      },
    ],
  };
}

/** Builds radial progress dial for scalar %/ratio channels */
export function radialOption(w: any, precision: number, units: any, viewport?: { spanMs: number; offsetMs: number }): any {
  const first = w.parameters?.[0] || { subsystem: '', paramId: '', paramName: '' };
  const def = getChannelDef(first.subsystem, first.paramId || first.paramName);
  const v = conv(latestAtViewport(w, viewport), def, units);
  const min = conv((def as any).gMin != null ? (def as any).gMin : (def.min != null ? def.min : 0), def, units)!;
  const max = conv((def as any).gMax != null ? (def as any).gMax : (def.max != null ? def.max : 100), def, units)!;
  const lvl = alertLevel(w);
  const col = lvl === 'crit' ? '#FF3333' : lvl === 'warn' ? '#E79E2C' : '#CCF54E';

  return {
    series: [
      {
        type: 'gauge',
        min,
        max,
        startAngle: 90,
        endAngle: -270,
        radius: '82%',
        center: ['50%', '52%'],
        progress: { show: true, width: 14, itemStyle: { color: col } },
        pointer: { show: false },
        axisLine: { lineStyle: { width: 14, color: [[1, 'rgba(255,255,255,.08)']] } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        detail: {
          fontSize: 28,
          fontFamily: 'Space Grotesk',
          color: '#EEE',
          offsetCenter: [0, 0],
          formatter: (x: number) => {
            const enumVal = resolveEnumValue(x, def?.enumValues);
            if (enumVal) return `${enumVal} (${x})`;
            const u = cleanUnit(unitOf(def, units) || def?.unit || '');
            return x.toFixed(precision) + (u ? (u === '%' ? '%' : ` ${u}`) : '');
          },
        },
        data: [{ value: v }],
      },
    ],
  };
}

/** Builds pie chart for categorical or multi-parameter channel telemetry */
export function pieOption(w: any, timeframe: number, precision: number, units: any, viewport?: { spanMs: number; offsetMs: number }): any {
  const params = w.parameters || [];
  const first = params[0] || { subsystem: '', paramId: '', uniqueId: '' };
  const def = getChannelDef(first);

  const sub = first.subsystem || '';
  const paramId = first.paramId || first.paramName || '';
  const colonId = first.uniqueId || `${sub}:${paramId}`;
  const store = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
  const data = store?.data || [];
  const lastPt = data.length > 0 ? data[data.length - 1] : null;
  const rawVal = lastPt ? lastPt.value[1] : null;
  const tmType = getPointTmType(lastPt, store);

  let pieData: { name: string; value: number; itemStyle?: any; paramLabel?: string; seriesName?: string; unit?: string; tmType?: string }[] = [];

  const seriesNames = def.series || [];

  if (params.length > 1) {
    // Rule 1: Multiple parameters -> Name slices by parameter label
    pieData = params.map((p: any, i: number) => {
      const pDef = getChannelDef(p);
      const pColonId = p.uniqueId || `${p.subsystem}:${p.paramName}`;
      const pStore = findTelemetrySeriesStore(p.subsystem, p.paramName, pColonId, w.id);
      const pLastPt = pStore && pStore.data && pStore.data.length > 0 ? pStore.data[pStore.data.length - 1] : null;
      const pRawVal = pLastPt ? pLastPt.value?.[1] : 0;
      const pNumVal = Array.isArray(pRawVal) ? pRawVal[p.si ?? 0] : (typeof pRawVal === 'number' ? pRawVal : 0);
      const val = conv(typeof pNumVal === 'number' ? pNumVal : 0, pDef, units) || 0;
      const label = p.label || pDef.label || 'Parameter';
      return {
        name: label,
        value: Math.max(0, Math.abs(val)),
        itemStyle: { color: getTelemetrySeriesColor(i) },
        paramLabel: label,
        seriesName: '',
        unit: unitOf(pDef, units) || pDef.unit || '',
        tmType: getPointTmType(pLastPt, pStore),
      };
    });
  } else if (seriesNames.length > 0) {
    // Rule 2: Single parameter with multiple series -> Name slices by series item
    if (Array.isArray(rawVal)) {
      pieData = seriesNames.map((nm, i) => {
        const numVal = typeof rawVal[i] === 'number' ? rawVal[i] : 0;
        const val = conv(numVal, def, units) || 0;
        return {
          name: nm,
          value: Math.max(0, Math.abs(val)),
          itemStyle: { color: getTelemetrySeriesColor(i) },
          paramLabel: def.label,
          seriesName: nm,
          unit: unitOf(def, units) || def.unit || '',
          tmType,
        };
      });
    } else if (typeof rawVal === 'number') {
      pieData = seriesNames.map((nm, i) => {
        const numVal = i === 0 ? rawVal : 0;
        const val = conv(numVal, def, units) || 0;
        return {
          name: nm,
          value: Math.max(0, Math.abs(val)),
          itemStyle: { color: getTelemetrySeriesColor(i) },
          paramLabel: def.label,
          seriesName: nm,
          unit: unitOf(def, units) || def.unit || '',
          tmType,
        };
      });
    }
  } else {
    // Rule 3: Single scalar parameter -> Single slice with parameter label
    const valNum = typeof rawVal === 'number' ? rawVal : (Array.isArray(rawVal) ? rawVal[0] : 0);
    const val = conv(typeof valNum === 'number' ? valNum : 0, def, units) || 0;
    const label = first.label || def.label || 'Parameter';

    pieData = [
      {
        name: label,
        value: Math.max(0, Math.abs(val)),
        itemStyle: { color: getTelemetrySeriesColor(0) },
        paramLabel: label,
        seriesName: '',
        unit: unitOf(def, units) || def.unit || '',
        tmType,
      },
    ];
  }

  const finalData = pieData.length > 0 ? pieData : [{ name: 'No Data', value: 0, paramLabel: 'No Data', tmType: 'TM' }];
  const showLegend = finalData.length > 1;

  return {
    tooltip: {
      trigger: 'item',
      ...tooltipStyle(),
      formatter: (item: any) => {
        const marker = makeTooltipMarker(item.color || '#00E5FF');
        const dataObj = item.data;
        const paramLabel = dataObj?.paramLabel || item.name || 'Parameter';
        const seriesName = dataObj?.seriesName || '';
        const displayTitle = formatTooltipSeriesTitle(paramLabel, seriesName, item.name);
        const badge = makeTmTypeBadge(dataObj?.tmType || tmType);
        const val = item.value != null ? (typeof item.value === 'number' ? item.value.toFixed(precision) : item.value) : 0;
        const unit = dataObj?.unit || unitOf(def, units) || '';
        const pct = item.percent != null ? ` (${item.percent.toFixed(1)}%)` : '';
        const valStr = `${val}${unit ? ' ' + unit : ''}${pct}`;

        return makeTooltipHeader(displayTitle) + makeTooltipRow(marker, '', badge, valStr);
      },
    },
    legend: {
      show: showLegend,
      type: 'scroll',
      bottom: 0,
      textStyle: { color: '#B4B4B4', fontSize: 10, fontFamily: 'Montserrat' },
      itemWidth: 10,
      itemHeight: 10,
    },
    series: [
      {
        type: 'pie',
        radius: ['46%', '72%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: '#0d1318', borderWidth: 2 },
        label: { show: false },
        labelLine: { show: false },
        data: finalData,
      },
    ],
  };
}

/**
 * Shortens and truncates bar chart category axis labels to prevent visual overlapping
 * while preserving identifying indices/numbers and readability.
 */
export function formatBarAxisCategoryLabel(label: string, count: number, horiz: boolean): string {
  if (!label || typeof label !== 'string') return '';
  const trimmed = label.trim();

  // Uniform truncation to 14 characters across both vertical and horizontal bar charts
  const maxLen = 14;

  // Apply clean domain-specific abbreviations for aerospace/telemetry terms
  const abbreviated = trimmed
    .replace(/Temperature/gi, 'Temp')
    .replace(/Current/gi, 'Curr')
    .replace(/Voltage/gi, 'Volt')
    .replace(/Battery/gi, 'Bat');

  if (abbreviated.length <= maxLen) {
    return abbreviated;
  }

  // Fallback: truncate with ellipsis to fit within 14 chars
  return `${abbreviated.slice(0, maxLen - 1).trim()}…`;
}

/** Builds vertical or horizontal bar chart for categorical or multi-parameter channel telemetry */
export function barOption(w: any, horiz: boolean, timeframe: number, precision: number, units: any, viewport?: { spanMs: number; offsetMs: number }): any {
  const params = w.parameters || [];
  const first = params[0] || { subsystem: '', paramId: '', uniqueId: '' };
  const def = getChannelDef(first);

  const sub = first.subsystem || '';
  const paramId = first.paramId || first.paramName || '';
  const colonId = first.uniqueId || `${sub}:${paramId}`;
  const store = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
  const data = store?.data || [];
  const lastPt = data.length > 0 ? data[data.length - 1] : null;
  const rawVal = lastPt ? lastPt.value[1] : null;
  const tmType = getPointTmType(lastPt, store);

  let barCategories: string[] = [];
  let barSeriesData: any[] = [];

  const seriesNames = def.series || [];

  if (params.length > 1) {
    // Rule 1: Multiple parameters -> Categories = Parameter Labels
    barCategories = params.map((p: any) => p.label || getChannelDef(p).label || 'Parameter');
    barSeriesData = params.map((p: any, i: number) => {
      const pDef = getChannelDef(p);
      const pColonId = p.uniqueId || `${p.subsystem}:${p.paramName || p.tel}`;
      const pStore = findTelemetrySeriesStore(p.subsystem, p.paramName || p.tel, pColonId, w.id);
      const pLastPt = pStore && pStore.data && pStore.data.length > 0 ? pStore.data[pStore.data.length - 1] : null;
      const pRawVal = pLastPt ? pLastPt.value?.[1] : 0;
      const pNumVal = Array.isArray(pRawVal) ? pRawVal[p.si ?? 0] : (typeof pRawVal === 'number' ? pRawVal : 0);
      const val = conv(typeof pNumVal === 'number' ? pNumVal : 0, pDef, units) || 0;
      const label = barCategories[i];
      return {
        value: val,
        itemStyle: {
          color: getTelemetrySeriesColor(i),
          borderRadius: horiz ? [0, 3, 3, 0] : [3, 3, 0, 0],
        },
        paramLabel: label,
        seriesName: '',
        unit: unitOf(pDef, units) || pDef.unit || '',
        tmType: getPointTmType(pLastPt, pStore),
      };
    });
  } else if (seriesNames.length > 0) {
    // Rule 2: Single parameter with multiple series -> Categories = Series Items
    barCategories = seriesNames;
    barSeriesData = seriesNames.map((sName, i) => {
      const numVal = Array.isArray(rawVal) ? (typeof rawVal[i] === 'number' ? rawVal[i] : 0) : (i === 0 && typeof rawVal === 'number' ? rawVal : 0);
      const val = conv(numVal, def, units) || 0;
      return {
        value: val,
        itemStyle: {
          color: getTelemetrySeriesColor(i),
          borderRadius: horiz ? [0, 3, 3, 0] : [3, 3, 0, 0],
        },
        paramLabel: def.label,
        seriesName: sName,
        unit: unitOf(def, units) || def.unit || '',
        tmType,
      };
    });
  } else {
    // Rule 3: Single scalar parameter -> Category = Parameter Label
    const label = first.label || def.label || 'Parameter';
    const valNum = typeof rawVal === 'number' ? rawVal : (Array.isArray(rawVal) ? rawVal[0] : 0);
    const val = conv(typeof valNum === 'number' ? valNum : 0, def, units) || 0;
    barCategories = [label];
    barSeriesData = [
      {
        value: val,
        itemStyle: {
          color: getTelemetrySeriesColor(0),
          borderRadius: horiz ? [0, 3, 3, 0] : [3, 3, 0, 0],
        },
        paramLabel: label,
        seriesName: '',
        unit: unitOf(def, units) || def.unit || '',
        tmType,
      },
    ];
  }

  const count = barCategories.length;
  const isDense = !horiz && count > 5;
  const isVeryDense = !horiz && count > 10;
  const rotateAngle = !horiz ? (isVeryDense ? 45 : (isDense ? 35 : 0)) : 0;

  const showLegend = barCategories.length > 1;

  const commonUnit = cleanUnit(
    (barSeriesData.find((d) => d.unit)?.unit) ||
    unitOf(def, units) ||
    def?.unit ||
    ''
  );

  const catAxis = {
    type: 'category',
    data: barCategories,
    triggerEvent: true,
    ...baseAxis(),
    axisLabel: {
      color: '#B4B4B4',
      fontFamily: 'Montserrat',
      fontSize: 10,
      interval: 0,
      width: horiz ? 90 : undefined,
      overflow: 'truncate',
      ellipsis: '…',
      margin: horiz ? 8 : 4,
      rotate: rotateAngle,
      align: horiz ? 'right' : (rotateAngle > 0 ? 'right' : 'center'),
      verticalAlign: horiz ? 'middle' : (rotateAngle > 0 ? 'middle' : 'top'),
      formatter: (label: string) => formatBarAxisCategoryLabel(label, count, horiz),
    },
  };

  const valAxis = {
    type: 'value',
    ...baseAxis(),
    name: horiz ? undefined : (commonUnit || undefined),
    nameGap: 8,
    nameLocation: 'end',
    boundaryGap: horiz ? [0, 0] : ['0%', '20%'],
    min: horiz ? (val: { min: number }) => (val.min < 0 ? val.min : 0) : undefined,
    max: horiz ? (val: { max: number }) => (val.max > 0 ? val.max : 1) : undefined,
    nameTextStyle: {
      color: '#B4B4B4',
      fontSize: 9,
      fontFamily: 'Fira Mono',
      align: horiz ? 'right' : 'left',
    },
    axisLabel: {
      color: '#B4B4B4',
      fontFamily: 'Fira Mono',
      fontSize: 9,
      formatter: (v: number) => v.toFixed(precision),
    },
  };

  return {
    grid: {
      left: horiz ? 10 : 20,
      right: horiz ? 100 : 16,
      top: horiz ? 16 : (commonUnit && !horiz ? 28 : 20),
      bottom: horiz ? 16 : 6,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        axis: horiz ? 'y' : 'x',
      },
      ...tooltipStyle(),
      formatter: (items: any[]) => {
        const itemArr = Array.isArray(items) ? items : (items ? [items] : []);
        if (itemArr.length === 0) return '';
        const primaryItem = itemArr[0];
        const catName = primaryItem.name || primaryItem.data?.paramLabel || primaryItem.data?.seriesName || '';
        const lastPtTime = lastPt && Array.isArray(lastPt.value) && typeof lastPt.value[0] === 'number' ? lastPt.value[0] : null;
        const headerTime = lastPtTime ? fmtTooltipTimestamp(lastPtTime, timeframe * 60000) : '';
        let html = headerTime ? makeTooltipHeader(headerTime) : '';

        // For a categorical bar chart, render exactly one clean row for the selected bar
        const marker = makeTooltipMarker(primaryItem.color || primaryItem.data?.itemStyle?.color || '#00E5FF');
        const dataObj = primaryItem.data;
        const badge = makeTmTypeBadge(dataObj?.tmType || tmType);
        const val = primaryItem.value != null ? (typeof primaryItem.value === 'number' ? primaryItem.value.toFixed(precision) : primaryItem.value) : 0;
        const uStr = cleanUnit(dataObj?.unit || unitOf(def, units) || commonUnit || '');
        const valStr = `${val}${uStr ? ' ' + uStr : ''}`;
        html += makeTooltipRow(marker, catName, badge, valStr);

        return html;
      },
    },
    legend: {
      show: showLegend,
      type: 'scroll',
      top: 2,
      textStyle: { color: '#B4B4B4', fontSize: 10, fontFamily: 'Montserrat' },
    },
    xAxis: horiz ? valAxis : catAxis,
    yAxis: horiz ? { ...catAxis, inverse: true } : valAxis,
    series: [
      {
        type: 'bar',
        barWidth: horiz ? 14 : '44%',
        barMaxWidth: horiz ? 16 : 32,
        data: barSeriesData,
        label: {
          show: horiz || !isDense,
          position: horiz ? 'right' : 'top',
          distance: horiz ? 6 : 4,
          color: '#B4B4B4',
          fontFamily: 'Fira Mono',
          fontSize: 9,
          formatter: (params: any) => {
            const rawVal = params.value;
            const valStr = typeof rawVal === 'number' ? rawVal.toFixed(precision) : (rawVal ?? '');
            const u = cleanUnit(params.data?.unit || commonUnit);
            return u ? `${valStr} ${u}` : `${valStr}`;
          },
        },
      },
    ],
  };
}

function extractPointRawValue(pt: any): number {
  if (pt == null) return 0;
  if (typeof pt === 'number') return isNaN(pt) ? 0 : pt;
  if (typeof pt.rawVal === 'number' && !isNaN(pt.rawVal)) return pt.rawVal;
  if (Array.isArray(pt)) {
    const v = pt.length > 1 ? pt[1] : pt[0];
    return typeof v === 'number' && !isNaN(v) ? v : (Number(v) || 0);
  }
  if (Array.isArray(pt.value)) {
    const v = pt.value[1];
    if (typeof v === 'number' && !isNaN(v)) return v;
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  }
  if (typeof pt.value === 'number' && !isNaN(pt.value)) return pt.value;
  const num = Number(pt);
  return isNaN(num) ? 0 : num;
}

/** Builds radar chart for multi-parameter comparison */
export function radarOption(w: any, timeframe: number, precision: number, units: any, viewport?: { spanMs: number; offsetMs: number }): any {
  const first = w.parameters?.[0] || { subsystem: '', paramId: '' };
  const def = getChannelDef(first);
  const b = bundle(w, timeframe, viewport);

  let seriesList = b.series || [];

  if (seriesList.length === 0) {
    if (w.parameters && w.parameters.length > 1) {
      seriesList = w.parameters.map((p: any, idx: number) => ({
        name: p.label || getChannelDef(p).label || `Axis ${idx + 1}`,
        color: getTelemetrySeriesColor(idx),
        data: [],
        _def: getChannelDef(p),
      }));
    } else if (def.series && def.series.length > 0) {
      seriesList = def.series.map((sName: string, idx: number) => ({
        name: sName,
        color: getTelemetrySeriesColor(idx),
        data: [],
        _def: def,
      }));
    }
  }

  const vals = seriesList.map((s: any) => {
    const sDef = s?._def || def;
    let lastPt = s.data && s.data.length > 0 ? s.data[s.data.length - 1] : null;
    if (!lastPt) {
      const sub = (sDef as any)?.subsystem || first.subsystem || '';
      const pId = (sDef as any)?.paramId || (sDef as any)?.paramName || first.paramId || '';
      const uId = (sDef as any)?.uniqueId || `${sub}:${pId}`;
      const store = findTelemetrySeriesStore(sub, pId, uId, w.id);
      if (store?.data && store.data.length > 0) {
        lastPt = store.data[store.data.length - 1];
      }
    }
    const raw = lastPt != null ? extractPointRawValue(lastPt) : 0;
    const c = conv(raw, sDef, units);
    return typeof c === 'number' && !isNaN(c) ? c : raw;
  });

  const maxValFromData = Math.max(1, ...vals.map((v) => Math.abs(v))) * 1.25;

  const indsC = seriesList.map((s: any, idx: number) => {
    const sDef = s?._def || def;
    const val = Math.abs(vals[idx] || 0);
    const configuredMax = sDef?.max != null && typeof sDef.max === 'number' && sDef.max > 0 ? sDef.max : null;
    const convMax = configuredMax != null ? conv(configuredMax, sDef, units) : null;
    // Ensure axis max is strictly greater than the actual data value
    const effectiveMax = Math.max(
      convMax != null && !isNaN(convMax) && convMax > 0 ? convMax : 0,
      val * 1.25,
      maxValFromData,
      1
    );
    return {
      name: s.name || `Axis ${idx + 1}`,
      max: effectiveMax,
    };
  });

  let finalIndicators = indsC;
  if (finalIndicators.length === 0) {
    finalIndicators = [
      { name: 'Axis 1', max: 100 },
      { name: 'Axis 2', max: 100 },
      { name: 'Axis 3', max: 100 },
    ];
  } else if (finalIndicators.length === 1) {
    const mainMax = finalIndicators[0].max || 100;
    finalIndicators = [
      finalIndicators[0],
      { name: '', max: mainMax },
      { name: '', max: mainMax },
    ];
  } else if (finalIndicators.length === 2) {
    const mainMax = Math.max(finalIndicators[0].max || 100, finalIndicators[1].max || 100);
    finalIndicators = [
      finalIndicators[0],
      finalIndicators[1],
      { name: '', max: mainMax },
    ];
  }

  const paddedVals = [...vals];
  while (seriesList.length > 0 && paddedVals.length < finalIndicators.length) {
    paddedVals.push(0);
  }

  const radarData = seriesList.length > 0
    ? [
      {
        value: paddedVals,
        name: w.name || def.label || 'Radar',
        areaStyle: { color: 'rgba(204,245,78,.18)' },
        lineStyle: { color: '#CCF54E', width: 1.6 },
        itemStyle: { color: '#CCF54E' },
      },
    ]
    : [];

  return {
    tooltip: {
      ...tooltipStyle(),
      formatter: (params: any) => {
        const headerTitle = w.name || params?.name || def.label || 'Radar';
        let html = makeTooltipHeader(headerTitle);

        if (Array.isArray(params?.value)) {
          // Only render real parameters/series in the tooltip, ignore dummy geometry padding
          seriesList.forEach((s: any, idx: number) => {
            const v = params.value[idx];
            const sDef = s?._def || def;
            const indName = s?.name || indsC[idx]?.name || `Series ${idx + 1}`;
            const markerColor = s?.color || getTelemetrySeriesColor(idx);
            const marker = makeTooltipMarker(markerColor);

            let valueStr = '—';

            if (v != null && !isNaN(Number(v))) {
              const numVal = Number(v);
              const enumVal = resolveEnumValue(numVal, sDef?.enumValues);
              const enumState = enumVal ? isBooleanEnumState(enumVal) : null;

              if (enumState) {
                valueStr = makeEnumBadgeHtml(enumState.toUpperCase(), enumState, numVal);
              } else if (enumVal) {
                valueStr = `${enumVal} (${numVal})`;
              } else {
                const rawUnit = sDef ? (unitOf(sDef, units) || (sDef as any).unit || '') : '';
                const u = cleanUnit(rawUnit);
                const formattedNum = numVal.toFixed(precision);
                valueStr = u ? `${formattedNum} ${u}` : formattedNum;
              }
            }

            html += makeTooltipRow(marker, indName, '', valueStr);
          });
        }
        return html;
      },
    },
    radar: {
      indicator: finalIndicators,
      center: ['50%', '56%'],
      radius: '52%',
      axisName: { color: '#B4B4B4', fontSize: 10, fontFamily: 'Montserrat' },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,.08)' } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: 'rgba(255,255,255,.1)' } },
    },
    series: [
      {
        type: 'radar',
        data: radarData,
      },
    ],
  };
}

/** Builds composite chart option for custom multi-parameter streams */
export function multiChartOption(w: any, precision: number, units: any, viewport: { spanMs: number; offsetMs: number; customStartMs?: number | null }): any {
  const rows = multiChartWindow(w, units, viewport);
  const vp = viewport || { spanMs: 300000, offsetMs: 0 };
  const span = vp.spanMs;
  const ulist: string[] = [];

  const latestTimestamp = getWidgetLatestTimestamp(w);
  let startMs: number;
  let endMs: number;
  if (vp.customStartMs != null && vp.customStartMs > 0) {
    startMs = vp.customStartMs;
    endMs = startMs + span;
  } else {
    endMs = vp.offsetMs > 0 ? (latestTimestamp - vp.offsetMs) : latestTimestamp;
    startMs = endMs - span;

    const simStartMs = getGlobalSimulationStartTimeMs() || getTelemetryEarliestTimestamp(w);
    if (simStartMs && simStartMs > 0 && endMs > simStartMs) {
      startMs = Math.max(startMs, simStartMs);
    }
  }

  rows.forEach((r) => {
    if (ulist.indexOf(r.unit) < 0 && ulist.length < CHART_MAX_UNITS) ulist.push(r.unit);
  });

  const unitColor: Record<string, string> = {};
  rows.forEach((r) => {
    if (unitColor[r.unit] == null) unitColor[r.unit] = r.color;
  });

  const meta = rows.map((r) => ({
    name: tableColLabel(r.c),
    color: r.color,
    unit: r.unit,
  }));

  const ecs = rows.map((r, i) => {
    let ui = ulist.indexOf(r.unit);
    if (ui < 0) ui = 0;
    const paramForCol = (w.parameters || []).find((p: any) => {
      const pSub = p.subsystem || '';
      const pId = p.paramId || p.paramName || '';
      const pLabel = p.label || tableColLabel(p);
      const cSub = r.c?.subsystem || '';
      const cId = r.c?.paramId || r.c?.paramName || '';
      const cLabel = r.c?.label || tableColLabel(r.c);
      return (
        (p.uniqueId && p.uniqueId === r.c?.uniqueId) ||
        (pSub && pId && pSub === cSub && pId === cId) ||
        pLabel === cLabel ||
        pLabel === meta[i]?.name
      );
    }) || (w.parameters && w.parameters[i]) || r.c;

    const sDef = tableColDef(r.c);
    const pLabel = (w.parameters || []).length > 1 ? (meta[i]?.name || r.c?.label) : undefined;
    const thData = thresholdMarkLine(paramForCol, sDef, units, pLabel);

    return {
      name: meta[i].name,
      type: 'line',
      showSymbol: false,
      connectNulls: false,
      smooth: false,
      yAxisIndex: ui,
      lineStyle: { width: 1.6, color: r.color },
      itemStyle: { color: r.color },
      emphasis: { focus: 'series' },
      areaStyle: w.graphMode === 'area'
        ? {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: r.color + '40' },
              { offset: 1, color: r.color + '02' },
            ],
          },
        }
        : null,
      data: r.data,
      markLine: thData.length > 0
        ? { silent: true, symbol: 'none', data: thData }
        : undefined,
    };
  });

  const yAxes = ulist.length
    ? ulist.map((u, i) => {
      const pos = i % 2 === 0 ? 'left' : 'right';
      const off = i >= 2 ? 46 : 0;
      const col = unitColor[u] || '#B4B4B4';

      return {
        type: 'value',
        scale: true,
        splitNumber: 4,
        position: pos,
        offset: off,
        name: u,
        nameGap: 8,
        nameLocation: 'end',
        nameTextStyle: {
          color: '#B4B4B4',
          fontFamily: 'Fira Mono',
          fontSize: 9,
          align: pos === 'left' ? 'left' : 'right',
        },
        axisLine: { show: true, lineStyle: { color: col } },
        axisTick: { show: false },
        axisLabel: {
          color: col,
          fontFamily: 'Fira Mono',
          fontSize: 9,
          hideOverlap: true,
          margin: 6,
          formatter: (v: number) => v.toFixed(precision),
        },
        splitLine: { show: i === 0, lineStyle: { color: 'rgba(255,255,255,.05)' } },
      };
    })
    : [{ type: 'value', ...baseAxis(), axisLabel: { color: '#B4B4B4', fontFamily: 'Fira Mono', fontSize: 9, formatter: (v: number) => v.toFixed(precision) } }];

  const Ln = (ulist.length >= 1 ? 1 : 0) + (ulist.length >= 3 ? 1 : 0);
  const Rn = (ulist.length >= 2 ? 1 : 0) + (ulist.length >= 4 ? 1 : 0);

  return {
    toolbox: getToolboxConfig(85, 0),
    grid: {
      left: ulist.length > 1 ? 20 + (Ln - 1) * 36 : 20,
      right: ulist.length > 1 ? 12 + (Rn - 1) * 36 : 16,
      top: 26,
      bottom: ecs.length > 1 ? 36 : 28,
      containLabel: true,
    },
    tooltip: createChartTooltip(precision, undefined, units, meta, span),
    legend: getLegendConfig(ecs),
    xAxis: {
      type: 'time',
      min: startMs,
      max: endMs,
      ...baseAxis(),
      axisLabel: {
        color: '#B4B4B4',
        fontFamily: 'Fira Mono',
        fontSize: 9,
        hideOverlap: true,
        formatter: (val: number) => fmtWinTime(val, span),
      },
    },
    yAxis: yAxes,
    series: ecs,
  };
}

/** Extracts multi-chart parameter data window */
export function multiChartWindow(w: any, units: any, viewport: { spanMs: number; offsetMs: number; customStartMs?: number | null }) {
  const first = w.parameters?.[0] || { subsystem: '', paramId: '' };
  const firstSub = first.subsystem || '';
  const firstParamId = first.paramId || first.paramName || '';
  const cols: Array<any> = resolveTableColumns(w.parameters && w.parameters.length > 0 ? w : { parameters: makeColumns(firstSub, firstParamId) });

  const hidden = new Set(w.hiddenCols || []);
  const full = cols;
  const vis = cols.filter((c) => !hidden.has(colKey(c)));

  const vp = viewport || { spanMs: 300000, offsetMs: 0 };

  return vis.map((c) => {
    const def = tableColDef(c);
    const cidx = full.findIndex((x) => colKey(x) === colKey(c));
    const color = getTelemetrySeriesColor(cidx < 0 ? 0 : cidx);

    const sub = c.subsystem || '';
    const paramId = c.paramId || c.paramName || '';
    const colonId = c.uniqueId || `${sub}:${paramId}`;
    const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);

    const data: any[] = [];

    if (found && found.data && found.data.length > 0) {
      const latestTimestamp = getWidgetLatestTimestamp(w);
      let startMs: number;
      let endMs: number;
      if (vp.customStartMs != null && vp.customStartMs > 0) {
        startMs = vp.customStartMs;
        endMs = startMs + vp.spanMs;
      } else {
        endMs = vp.offsetMs > 0 ? (latestTimestamp - vp.offsetMs) : latestTimestamp;
        startMs = endMs - vp.spanMs;

        const simStartMs = getGlobalSimulationStartTimeMs() || getTelemetryEarliestTimestamp(w);
        if (simStartMs && simStartMs > 0 && endMs > simStartMs) {
          startMs = Math.max(startMs, simStartMs);
        }
      }

      found.data.forEach((pt) => {
        if (!pt || !pt.value || typeof pt.value[0] !== 'number') return;
        let t = pt.value[0];
        if (t < 1e11) t = t * 1000;
        if (t >= startMs && t <= endMs) {
          const val = pt.value[1];
          let numVal: number | null = null;
          if (typeof val === 'number') {
            numVal = val;
          } else if (Array.isArray(val)) {
            numVal = typeof val[c.si ?? 0] === 'number' ? val[c.si ?? 0] : null;
          }
          const convVal = numVal !== null ? conv(numVal, def, units) : null;
          data.push({
            value: [t, convVal],
            rawVal: numVal,
            paramLabel: (c as any).label || def.label || 'Parameter',
            seriesName: (c.series || def.series)?.[c.si ?? 0] || tableColLabel(c),
            unit: unitOf(def, units) || def.unit || '',
            tmType: getPointTmType(pt, found),
            _def: def,
          });
        }
      });
    }

    return { c, def, color, unit: unitOf(def, units), data };
  });
}

/** Minimal sparkline background chart for KPI cards */
export function sparkOption(
  w: any,
  timeframe: number,
  units: any,
  viewport?: { spanMs: number; offsetMs: number }
): any {
  const first = w.parameters?.[0] || { subsystem: '', paramId: '' };
  const sub = first.subsystem || '';
  const paramId = first.paramId || first.paramName || '';
  const def = getChannelDef(sub, paramId);
  const b = bundle(w, timeframe, viewport);
  const d = (b.series[0]?.data || []).map((item: any) => {
    const raw = typeof item === 'object' && item !== null ? item.rawVal : item;
    return conv(raw, def, units);
  });
  const lvl = alertLevel(w);
  const col = lvl === 'crit' ? '#FF6A6A' : lvl === 'warn' ? '#F2A93B' : '#CCF54E';

  return {
    grid: { left: 0, right: 0, top: 2, bottom: 2 },
    xAxis: { type: 'category', show: false, boundaryGap: false, data: d.map((_: any, i: number) => i) },
    yAxis: { type: 'value', show: false, scale: true },
    series: [
      {
        type: 'line',
        data: d,
        showSymbol: false,
        smooth: true,
        lineStyle: { width: 1.8, color: col },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: col + '25' },
              { offset: 1, color: col + '00' },
            ],
          },
        },
      },
    ],
  };
}

/** Computes signature of all parameters in a widget */
function computeParamsSig(w: any): string {
  const params = w.parameters || [];
  if (params.length === 0) {
    const first = { subsystem: '', paramId: '' };
    const sub = first.subsystem || '';
    const paramId = first.paramId || '';
    const colonId = sub && paramId ? `${sub}:${paramId}` : '';
    const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
    const dataLen = found?.data?.length || 0;
    const lastTs = dataLen > 0 ? (found?.data[dataLen - 1]?.value?.[0] || 0) : 0;
    return `${sub}:${paramId}::${dataLen}:${lastTs}`;
  }

  return params
    .map((p: any) => {
      const sub = p.subsystem || '';
      const paramId = p.paramId || p.paramName || '';
      const colonId = p.uniqueId || (sub && paramId ? `${sub}:${paramId}` : '');
      const found = findTelemetrySeriesStore(sub, paramId, colonId, w.id);
      const dataLen = found?.data?.length || 0;
      const lastTs = dataLen > 0 ? (found?.data[dataLen - 1]?.value?.[0] || 0) : 0;
      const thOn = p.threshold?.on !== false ? '1' : '0';
      return `${sub}:${paramId}:${p.si ?? ''}:${dataLen}:${lastTs}:${thOn}`;
    })
    .join(';');
}

/** Compute chart signatures for ECharts setOption diffing */
export function chartSig(
  w: any,
  timeframe: number,
  precision: number,
  units: any,
  viewport: { spanMs: number; offsetMs: number }
): string {
  const hiddenStr = (w.hiddenCols || []).join(',');
  const paramsStr = computeParamsSig(w);
  return `${w.id}:${w.graphMode}:${w.name || ''}:${timeframe}:${precision}:${JSON.stringify(units)}:${viewport?.spanMs}:${viewport?.offsetMs}:${hiddenStr}:${paramsStr}`;
}

export function multiChartSig(
  w: any,
  precision: number,
  units: any,
  viewport: { spanMs: number; offsetMs: number }
): string {
  const hiddenStr = (w.hiddenCols || []).join(',');
  const paramsStr = computeParamsSig(w);
  return `${w.id}:cchart:${w.name || ''}:${precision}:${JSON.stringify(units)}:${viewport?.spanMs}:${viewport?.offsetMs}:${hiddenStr}:${paramsStr}`;
}

/** Master option builder dispatcher */
export function optionFor(
  w: any,
  timeframe: number,
  precision: number,
  units: any,
  viewport: { spanMs: number; offsetMs: number }
): any {
  let opt: any;
  switch (w.graphMode) {
    case 'line':
      opt = timeSeriesOption(w, false, timeframe, precision, units, viewport);
      break;
    case 'area':
      opt = timeSeriesOption(w, true, timeframe, precision, units, viewport);
      break;
    case 'gauge':
      opt = gaugeOption(w, precision, units, timeframe, viewport);
      break;
    case 'radial':
      opt = radialOption(w, precision, units, viewport);
      break;
    case 'pie':
      opt = pieOption(w, timeframe, precision, units, viewport);
      break;
    case 'bar':
      opt = barOption(w, false, timeframe, precision, units, viewport);
      break;
    case 'hbar':
      opt = barOption(w, true, timeframe, precision, units, viewport);
      break;
    case 'radar':
      opt = radarOption(w, timeframe, precision, units, viewport);
      break;
    case 'cchart':
      opt = multiChartOption(w, precision, units, viewport);
      break;
    default:
      opt = timeSeriesOption(w, false, timeframe, precision, units, viewport);
      break;
  }

  // Preserve user selected / deselected legends across horizontal scrolling and viewport updates
  if (opt && opt.legend && w?.id) {
    const saved = getPanelLegendSelected(w.id);
    if (saved && Object.keys(saved).length > 0) {
      if (Array.isArray(opt.legend)) {
        opt.legend[0].selected = { ...(opt.legend[0].selected || {}), ...saved };
      } else {
        opt.legend.selected = { ...(opt.legend.selected || {}), ...saved };
      }
    }
  }

  return opt;
}

export const getTelemetryChartOption = optionFor;

